import type {RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw} from 'vue-router';
import {createRouter, createWebHistory} from 'vue-router';

// Views
import Login from '@/views/Login.vue';
import Main from '@/views/Main.vue';
import Register from '@/views/Register.vue';
import ForgotPassword from '@/views/ForgotPassword.vue';
import ResetPassword from '@/views/ResetPassword.vue';
import Dashboard from '@/views/Dashboard.vue';
import CreateRunner from '@/views/CreateRunner.vue';
import EditRunner from '@/views/EditRunner.vue';
import ExternalRunnerForm from '@/views/ExternalRunnerForm.vue';
import RunnerAutoscaling from '@/views/RunnerAutoscaling.vue';
import Profile from '@/views/Profile.vue';
import Billing from '@/views/Billing.vue';
import BillingReturn from '@/views/BillingReturn.vue';
import OrganizationSettings from '@/views/OrganizationSettings.vue';
import AcceptInvitation from '@/views/AcceptInvitation.vue';
import OAuthConsent from '@/views/OAuthConsent.vue';
import McpHandoff from '@/views/McpHandoff.vue';
import Caches from '@/views/Caches.vue';
import CacheForm from '@/views/CacheForm.vue';
import Metrics from '@/views/Metrics.vue';
import MetricsConnectionForm from '@/views/MetricsConnectionForm.vue';
import {useAuthStore} from '@/stores/auth';
import {useOrganizationStore} from '@/stores/organization';
import {useSubscriptionStore} from '@/stores/subscription';
import {handoffLinkGuard} from '@/lib/mcp-handoff-link';

const routes = [
  {
    path: '/',
    component: Main,
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard,
      },
      {
        path: 'runner/create',
        name: 'CreateRunner',
        component: CreateRunner,
      },
      {
        path: 'runner/:id/edit',
        name: 'EditRunner',
        component: EditRunner,
      },
      {
        path: 'runner/:id/autoscaling',
        name: 'RunnerAutoscaling',
        component: RunnerAutoscaling,
      },
      {
        path: 'external-runners/new',
        name: 'CreateExternalRunner',
        component: ExternalRunnerForm,
      },
      {
        path: 'external-runners/:id/edit',
        name: 'EditExternalRunner',
        component: ExternalRunnerForm,
      },
      {
        path: 'external-runners/:id/autoscaling',
        name: 'ExternalRunnerAutoscaling',
        component: RunnerAutoscaling,
      },
      {
        path: 'caches',
        name: 'Caches',
        component: Caches,
      },
      {
        path: 'caches/new',
        name: 'CreateCache',
        component: CacheForm,
      },
      {
        path: 'caches/:id/edit',
        name: 'EditCache',
        component: CacheForm,
      },
      {
        path: 'metrics',
        name: 'Metrics',
        component: Metrics,
      },
      {
        path: 'metrics/new',
        name: 'CreateMetricsConnection',
        component: MetricsConnectionForm,
      },
      {
        path: 'metrics/:id/edit',
        name: 'EditMetricsConnection',
        component: MetricsConnectionForm,
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile,
      },
      {
        path: 'organization',
        name: 'OrganizationSettings',
        component: OrganizationSettings,
      },
      {
        path: 'billing',
        name: 'Billing',
        component: Billing,
      },
      {
        path: 'billing/return',
        name: 'BillingReturn',
        component: BillingReturn,
      },
    ],
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
  },
  {
    path: '/invitations/accept',
    name: 'AcceptInvitation',
    component: AcceptInvitation,
  },
  {
    path: '/oauth/consent',
    name: 'OAuthConsent',
    component: OAuthConsent,
  },
  {
    path: '/mcp/handoff',
    name: 'McpHandoff',
    component: McpHandoff,
  },
  {
    // keep backward-compatibility for existing links
    path: '/dashboard',
    redirect: { name: 'Dashboard' },
  },
] satisfies RouteRecordRaw[];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Toggle to temporarily disable the global auth guard (set to false to re-enable)
const DISABLE_AUTH_GUARD = false;

const publicAuthRoutes = ['Login', 'Register', 'ForgotPassword', 'ResetPassword'];
// Creating and editing runners needs an active plan of the selected organization. The Dashboard
// stays open without one, so running runners can still be paused and deleted.
const subscriptionRoutes = ['CreateRunner', 'EditRunner'];
const runnerMutationRoutes = [
  'CreateRunner',
  'EditRunner',
  'CreateCache',
  'EditCache',
  'CreateMetricsConnection',
  'EditMetricsConnection',
];

export async function authGuard(to: RouteLocationNormalized): Promise<RouteLocationRaw | boolean> {
  if (DISABLE_AUTH_GUARD) {
    return true;
  }

  const auth = useAuthStore();
  const name = to.name as string;

  // An invalid connection request is reported without asking the user to sign in first.
  if (name === 'OAuthConsent' && to.query.error) {
    return true;
  }

  // Redirect unauthenticated users to Login for any route except public auth pages
  if (!publicAuthRoutes.includes(name) && !auth.isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  // If already authenticated and trying to access public auth pages, go to Dashboard
  if (publicAuthRoutes.includes(name) && auth.isAuthenticated) {
    return { name: 'Dashboard' };
  }

  // A handoff names its own organization, so the selected one and its plan do not matter.
  if (
    !auth.isAuthenticated ||
    name === 'AcceptInvitation' ||
    name === 'OAuthConsent' ||
    name === 'McpHandoff'
  ) {
    return true;
  }

  // A change of session or selected organization while this guard waits always comes with a
  // newer navigation, so this one is cancelled instead of finishing for the old context.
  const token = auth.token;
  const organizations = useOrganizationStore();
  try {
    await organizations.ensureLoaded();
  } catch {
    // The organization switcher shows the error and offers a retry.
  }
  if (auth.token !== token) return false;

  if (runnerMutationRoutes.includes(name) && !organizations.canManageRunners) {
    return { name: 'Dashboard' };
  }

  if (subscriptionRoutes.includes(name)) {
    const subscription = useSubscriptionStore();
    const organizationId = organizations.selectedId;
    if (!subscription.subscription || subscription.organizationId !== organizationId) {
      try {
        await subscription.fetchSubscription();
      } catch {
        // Error state is tracked in store and handled via redirect below.
      }
      if (auth.token !== token || organizations.selectedId !== organizationId) return false;
      // A membership refresh meanwhile may have downgraded the role within the same organization.
      if (runnerMutationRoutes.includes(name) && !organizations.canManageRunners) {
        return { name: 'Dashboard' };
      }
    }
    if (!subscription.entitledAt()) {
      return { name: 'Billing' };
    }
  }

  return true;
}

router.beforeEach(handoffLinkGuard);
router.beforeEach(authGuard);

export default router;
